import { AxiosResponse } from 'axios';
import DoorAccess from '../classes/DoorAccess';
import LarvaConnectionConfig from '../classes/LarvaConnectionConfig';
import LarvaDoorAccessController from '../classes/LarvaDoorAccessController';
import LarvaWebClient from '../classes/LarvaWebClient';
import ILarvaSelectedDoorAccess from '../types/doorAccess/ILarvaSelectedDoorAccess';
import IDoorCardRawData from '../types/doorAccess/IDoorCardRawData';
import IDoorCodeRawData from '../types/doorAccess/IDoorCodeRawData';
import ILarvaDoorAccess from '../types/doorAccess/ILarvaDoorAccess';
import { isArray, isEmpty } from 'lodash';
import DoorCardRawData from '../classes/DoorCardRawData';
import DoorCodeRawData from '../classes/DoorCodeRawData';
import { DoorCardsResponse, DoorCodesResponse } from '../types/doorAccess/DoorAccessApiData';

const getControllerInstance = (connectionConfig: LarvaConnectionConfig): LarvaDoorAccessController => {
    return LarvaDoorAccessController.getInstance(new LarvaWebClient(connectionConfig));
};

export const sendPinCodeBySMS = async (
    access: DoorAccess,
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse | null> => {
    return await getControllerInstance(connectionConfig).sendPinCodeBySMS(access);
};

export const sendPinCodeByEmail = async (
    access: DoorAccess,
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse | null> => {
    return await getControllerInstance(connectionConfig).sendPinCodeByEmail(access);
};

export const unsetAccessPin = async (
    access: ILarvaSelectedDoorAccess,
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse<IDoorCodeRawData> | AxiosResponse<IDoorCardRawData> | null> => {
    return await getControllerInstance(connectionConfig).unsetAccessPin(access);
};

export const updateAccessPin = async (
    access: ILarvaSelectedDoorAccess,
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse<IDoorCodeRawData> | AxiosResponse<IDoorCardRawData> | null> => {
    return await getControllerInstance(connectionConfig).updateAccessPin(access);
};

export const createAccess = async (
    access: DoorAccess,
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse> => {
    return await getControllerInstance(connectionConfig).createAccess(access);
};

export const deleteAccess = async (
    access: DoorAccess,
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse> => {
    return await getControllerInstance(connectionConfig).deleteAccess(access);
};

export const updateAccessData = async (
    access: ILarvaDoorAccess,
    data: {
        [field: string]: string;
    },
    connectionConfig: LarvaConnectionConfig,
): Promise<AxiosResponse> => {
    return await getControllerInstance(connectionConfig).updateAccessData(access, data);
};

export const fetchDoorCards = async (connectionConfig: LarvaConnectionConfig): Promise<DoorCardsResponse> => {
    const cards = await getControllerInstance(connectionConfig).getCards();

    if (!isArray(cards) || isEmpty(cards)) {
        return {};
    }

    return cards.reduce((acc, card) => {
        return { ...acc, [card.id]: new DoorCardRawData(card) };
    }, {});
};

export const fetchDoorCodes = async (connectionConfig: LarvaConnectionConfig): Promise<DoorCodesResponse> => {
    const codes = await getControllerInstance(connectionConfig).getCodes();

    if (!isArray(codes) || isEmpty(codes)) {
        return {};
    }

    return codes.reduce((acc, code) => {
        return { ...acc, [code.id]: new DoorCodeRawData(code) };
    }, {});
};
